import React, { useState, useCallback, useMemo, useEffect } from 'react'
import { useAuthProvider, useCheckAuth, useLogout } from 'react-admin'
import { IsRequireAuthForAccessFn, ResetUserDataFn, UpdateUserDataFn, UserPayload } from './type'
import UserContext from './UserContext'
import { refreshUserInfo as getUserData } from '../../utils/user'
import { useLocation } from 'react-router-dom'
import keys from 'lodash/keys'
import config from '../../config'

export const UserContextProvider = ({ children }): JSX.Element => {
  const doLogout = useLogout()
  const checkAuth = useCheckAuth()
  const { setIdentityUser } = useAuthProvider()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [currentUserData, setCurrentUserData] = useState<UserPayload>(null)
  const [error, setError] = useState<Error | null>(null)
  const { pathname } = useLocation()

  const refreshUserinfo = useCallback(async () => {
    try {
      const updatedUserData = await getUserData()
      if (!updatedUserData) return
      setCurrentUserData(updatedUserData)
    } catch (err) {
      setCurrentUserData(null)
      setError(new Error('ra.auth.auth_check_error'))
    }
  }, [])

  const updateUserData: UpdateUserDataFn = useCallback((newUserData) => {
    setCurrentUserData(newUserData)
  }, [])

  const resetUserData: ResetUserDataFn = useCallback(() => {
    setCurrentUserData(null)
    setError(null)
  }, [])

  const restrictResources = useMemo(
    () => [...keys(config('resources.keys')), ...keys(config('pages.keys'))],
    [],
  )

  const isRequiredRoute: IsRequireAuthForAccessFn = useCallback(
    (proutePath) => {
      const currentResource = proutePath ? proutePath.split('/')[1] : ''
      return !currentResource ? true : restrictResources.includes(currentResource)
    },
    [restrictResources],
  )

  // Updating variable (memory cache) when user state was changed
  useEffect(() => {
    if (currentUserData) {
      setIdentityUser(currentUserData) // update the new data to memory cache variable
      setIsLoading(false)
    }
  }, [currentUserData, setIdentityUser])
  // useEffect:: Check authenticate only the restrict resources pages for refreshing user info
  useEffect(() => {
    // TODO:: Can fire new request to get currently permission when the route path was changed?
    if (isRequiredRoute(pathname)) {
      checkAuth({ page: pathname }, false)
        .then(() => {
          refreshUserinfo()
        })
        .catch((err) => {
          doLogout()
          resetUserData()
          setError(err)
        })
        .finally(() => setIsLoading(false))
    } else {
      resetUserData()
      setIsLoading(false)
    }
  }, [checkAuth, doLogout, isRequiredRoute, pathname, refreshUserinfo, resetUserData])

  const contextValue = useMemo(
    () => ({
      currentUserData,
      refreshUserinfo,
      updateUserData,
      resetUserData,
      isLoading,
      error,
    }),
    [currentUserData, error, isLoading, refreshUserinfo, resetUserData, updateUserData],
  )

  return <UserContext.Provider value={contextValue}>{children}</UserContext.Provider>
}
